export interface ExperienceLink {
  label: string;
  url: string;
  isPrimary?: boolean;
  icon?: string;
  badge?: string;
}

export interface Experience {
  id: 'racer' | 'ide' | 'fps';
  title: string;
  codename: string;
  category: 'GAME' | 'TOOL' | 'AUDIO';
  tagline: string;
  description: string;
  version: string;
  primaryUrl: string;
  themeColor: string;
  accentColor: string;
  cartridgeColor: string;
  specs: {
    engine: string;
    runtime: string;
    fps: string;
    features: string[];
    techStack: string[];
  };
  links: ExperienceLink[];
  stats: {
    label: string;
    value: string;
  }[];
  matrixCode: string[];
}

export const EXPERIENCES: Record<string, Experience> = {
  racer: {
    id: 'racer',
    title: 'TOILLE-RACER',
    codename: 'CYBER-RACER 2088',
    category: 'GAME',
    tagline: 'HIGH-VELOCITY TRACKMANIA-STYLE ARCADE RACER',
    description:
      'High-speed retro-futuristic arcade racing engine built with SolidJS, HTML5 Canvas, simulated 2D/3D stadium drift physics, AI ghost replays, dynamic Web Audio engine harmonics, wireless smartphone WebRTC gamepad, and detached telemetry cockpit.',
    version: 'v1.0.4',
    primaryUrl: 'https://racer.toille.uk',
    themeColor: '#ff007f', // Neon Hot Pink / Magenta
    accentColor: '#00fff2', // Neon Cyan
    cartridgeColor: '#ff0055',
    specs: {
      engine: 'Custom 2D/3D Hybrid Canvas Physics',
      runtime: 'SolidJS + WebRTC + Web Audio DSP',
      fps: '60 FPS Ultra-Low Latency',
      features: [
        'Stadium drift & grip physics simulation',
        'AI ghost replays with edge time trials',
        'Dual-screen telemetry cockpit (/telemetry)',
        'Wireless smartphone WebRTC gamepad pairing (/gamepad)',
        'In-browser interactive track editor',
        'Real-time procedural engine audio harmonics'
      ],
      techStack: ['SolidJS', 'TypeScript', 'HTML5 Canvas', 'Web Audio API', 'WebRTC DataChannel', 'Cloudflare D1']
    },
    links: [
      {
        label: 'LAUNCH RACER',
        url: 'https://racer.toille.uk',
        isPrimary: true,
        icon: '🏎️',
        badge: 'MAIN ARCADE'
      },
      {
        label: 'SMARTPHONE GAMEPAD',
        url: 'https://racer.toille.uk/gamepad',
        icon: '📱',
        badge: 'CONTROLLER'
      },
      {
        label: 'TELEMETRY COCKPIT',
        url: 'https://racer.toille.uk/telemetry',
        icon: '📊',
        badge: 'DUAL-SCREEN HUD'
      }
    ],
    stats: [
      { label: 'TOP VELOCITY', value: '380 KM/H' },
      { label: 'RENDER LOOP', value: '60.0 FPS' },
      { label: 'INPUT LATENCY', value: '< 4 MS' },
      { label: 'G-FORCE RADAR', value: '3.4 G' }
    ],
    matrixCode: [
      '> MOUNTING DRIVE /dev/track_racer0',
      '> CALIBRATING GYROSCOPIC SENSORS...',
      '> WEBRTC PEER CONNECTION: LISTENING',
      '> TIRE COEFFICIENT: 0.94 [DRIFT READY]',
      '> AUDIO OSCILLATOR: 4-CYL HARMONICS'
    ]
  },
  ide: {
    id: 'ide',
    title: 'TOILLE-IDE',
    codename: 'NEO-WORKSPACE VIM LAB',
    category: 'TOOL',
    tagline: 'BROWSER-NATIVE CODE WORKSPACE & WASM PLAYGROUND',
    description:
      'Zero-latency browser code laboratory for ESNext JavaScript and PHP WebAssembly execution. Features full Vim modal engine, Web Worker isolated sandboxes, live syntax diagnostics, real-time AST parameter hints, and multi-file virtual workspace.',
    version: 'v2.1.0',
    primaryUrl: 'https://ide.toille.uk',
    themeColor: '#00fff2', // Neon Cyan
    accentColor: '#39ff14', // Neon Green
    cartridgeColor: '#00bbff',
    specs: {
      engine: 'Isolated Web Worker + PHP WASM Sandbox',
      runtime: 'SolidJS + Web Workers + WebAssembly',
      fps: 'Instant Reactive Eval',
      features: [
        'Multi-language JS & PHP WASM execution',
        'Full Vim modal keybinding engine (Normal/Insert/Visual/Cmd)',
        'Real-time syntax highlighting & AST diagnostics',
        'Multi-file virtual workspace manager',
        'Console log stream capture & HTML preview',
        'Sharable URL snippet compression (deflate/base64url)'
      ],
      techStack: ['SolidJS', 'TypeScript', 'Web Workers', 'PHP WebAssembly', 'Vim Engine', 'Prism/Babel AST']
    },
    links: [
      {
        label: 'LAUNCH TOILLE-IDE',
        url: 'https://ide.toille.uk',
        isPrimary: true,
        icon: '⚡',
        badge: 'LIVE WORKSPACE'
      }
    ],
    stats: [
      { label: 'ENGINE', value: 'JS / PHP-WASM' },
      { label: 'SANDBOX', value: 'WORKER-ISOLATED' },
      { label: 'MODES', value: 'VIM / NORMAL' },
      { label: 'EVAL SPEED', value: '0.8 MS' }
    ],
    matrixCode: [
      '> ALLOCATING WASM MEMORY PAGE: 64MB',
      '> VIM EMULATION ENGINE: LOADED',
      '> WORKER SECURITY POLICY: ENFORCED',
      '> AST PARSER: READY FOR SYNTAX STREAM',
      '> EVALUATION THREAD: IDLE [LISTENING]'
    ]
  },
  fps: {
    id: 'fps',
    title: 'TOILLE-FPS',
    codename: 'UNREAL ARENA // DM-MORPHEUS',
    category: 'GAME',
    tagline: 'LOW-GRAVITY SCI-FI ARENA DEATHMATCH',
    description:
      'High-speed Unreal Tournament 99 style arena shooter engine built with Three.js, low-gravity physics, and Web Audio synthesis. Features the iconic DM-Morpheus skyscraper rooftop tri-tower arena, directional launch pads, autonomous AI bots (Xan, Loque, Malcolm, Brock, Lauren), full 6-weapon arsenal with Shock Combo, dynamic announcer, and real-time frag scoreboard.',
    version: 'v1.0.0',
    primaryUrl: 'https://fps.toille.uk',
    themeColor: '#ffaa00', // Neon Amber / Golden Visor
    accentColor: '#00fff2', // Neon Cyan Shock Beam
    cartridgeColor: '#e67300',
    specs: {
      engine: 'Custom Three.js 3D Physics & Raycast',
      runtime: 'TypeScript + Vite + Web Audio DSP',
      fps: '60+ FPS Low-G Combat',
      features: [
        'DM-Morpheus 3-skyscraper map replica',
        'Directional launch jump pads between towers',
        '6 UT weapons with full Shock Combo mechanics',
        'Autonomous bot pathfinding & combat AI',
        'Synthesized UT announcer voice banners',
        'Tab frag scoreboard & real-time killfeed'
      ],
      techStack: ['Three.js', 'TypeScript', 'Web Audio API', 'PointerLock API', 'Cloudflare Pages']
    },
    links: [
      {
        label: 'LAUNCH TOILLE-FPS',
        url: 'https://fps.toille.uk',
        isPrimary: true,
        icon: '🎯',
        badge: 'MAIN ARENA'
      }
    ],
    stats: [
      { label: 'GRAVITY', value: '-10.0 M/S²' },
      { label: 'BOT AI COUNT', value: '5 COMBATANTS' },
      { label: 'FRAG LIMIT', value: '20 FRAGS' },
      { label: 'WEAPONS', value: '6 ARSENAL' }
    ],
    matrixCode: [
      '> MOUNTING ARENA /dev/dm_morpheus',
      '> GRAVITY COEFFICIENT: 0.40G [LOW-G]',
      '> COMBAT BOT PROTOCOLS: ACTIVE [XAN, LOQUE]',
      '> SHOCK COMBO RESONATOR: CHARGED',
      '> ARENA STATUS: ALL SYSTEMS NOMINAL'
    ]
  }
};
